import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiCheck, FiX, FiPackage,
  FiDownload, FiAlertCircle, FiCalendar, FiUser,
  FiFileText, FiDollarSign, FiShoppingCart, FiMapPin, FiPlayCircle, FiEdit3,
  FiTrash2, FiSearch, FiPlus
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { ConfirmModal } from '../../components/UI/Modal';
import { useNotifications } from '../../components/UI/Notifications/NotificationProvider';
import { useAuth } from '../../contexts/AuthContext';
import { ordenesService, ordenUtils } from '../../services/ordenesService';
import { clientesService } from '../../services';
import { useProductos } from '../../hooks/useProductos';

const VentaDetalle = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSuccess, showError } = useNotifications();
  const { hasPermission } = useAuth();
  const canManageSales = hasPermission('ventas.update');

  // Confirmación genérica (reemplaza window.confirm)
  const [confirm, setConfirm] = useState(null);
  const acceptConfirm = async () => {
    const fn = confirm?.onConfirm;
    setConfirm(null);
    if (fn) await fn();
  };

  const [orden, setOrden] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSavingDiscount, setIsSavingDiscount] = useState(false);
  const [isSavingShipping, setIsSavingShipping] = useState(false);
  const [discountInput, setDiscountInput] = useState('0');
  const [discountType, setDiscountType] = useState('amount'); // 'amount' o 'percentage'
  const [shippingCostInput, setShippingCostInput] = useState('0');

  // Edición de los productos de la orden (ver puedeEditarProductos abajo).
  // Se trabaja sobre una copia: hasta que no se pulsa Guardar no se toca nada.
  const [editandoProductos, setEditandoProductos] = useState(false);
  const [lineasEditadas, setLineasEditadas] = useState([]);
  const [guardandoProductos, setGuardandoProductos] = useState(false);
  const [busquedaProducto, setBusquedaProducto] = useState('');

  // Buscador para agregar productos a la orden (mismo hook que usa OrdenForm)
  const { productos: productosEncontrados, searchProductos } = useProductos();
  useEffect(() => {
    if (editandoProductos && busquedaProducto.trim().length > 0) {
      searchProductos(busquedaProducto);
    }
    // searchProductos se omite a proposito: cambia en cada render del hook y
    // volveria a disparar la busqueda en bucle.
  }, [busquedaProducto, editandoProductos]);

  useEffect(() => {
    loadOrdenDetalle();
  }, [id]);

  const loadOrdenDetalle = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await ordenesService.getOrdenById(id);

      if (result.success) {
        setOrden(result.data);
        setDiscountInput(String(result.data.discount_amount || 0));
        setShippingCostInput(String(result.data.shipping_cost || 0));

        // El cliente viene embebido en la respuesta de la venta
        if (result.data.client) {
          setCliente(result.data.client);
        } else if (result.data.client_id) {
          // Fallback: si por alguna razón no viene embebido, cargar por separado
          const clienteResult = await clientesService.getCliente(result.data.client_id);
          if (clienteResult.success) {
            setCliente(clienteResult.data);
          }
        }
      } else {
        setError(result.message || 'Venta no encontrada');
      }
    } catch (error) {
      setError('Error de conexión al cargar Orden');
      console.error('Error loading sale:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setIsProcessing(true);
    try {
      const filename = `orden_${orden.number || id}.pdf`;
      const result = await ordenesService.downloadPdf(id, filename);
      if (!result.success) {
        setError(result.message || 'Error al generar el PDF');
      }
    } catch (err) {
      setError('Error al generar el PDF');
    } finally {
      setIsProcessing(false);
    }
  };

  // Calcula el monto de descuento según el tipo; devuelve {amount} o {error}
  const computeDiscountAmount = () => {
    const value = parseFloat(discountInput);
    if (isNaN(value) || value < 0) {
      return { error: 'Ingresa un valor de descuento válido (mayor o igual a 0)' };
    }
    if (discountType === 'percentage') {
      if (value > 100) return { error: 'El porcentaje de descuento no puede ser mayor a 100%' };
      return { amount: orden.subtotal * (value / 100) };
    }
    if (value > orden.subtotal) return { error: 'El descuento no puede ser mayor al subtotal' };
    return { amount: value };
  };

  const applyUpdatedTotals = (result) => {
    const updated = result.data?.sale;
    if (updated) {
      setOrden(prev => ({
        ...prev,
        discount_amount: updated.discount_amount,
        shipping_cost: updated.shipping_cost,
        total: updated.total
      }));
    }
  };

  const handleSaveDiscount = async () => {
    const { amount, error: dErr } = computeDiscountAmount();
    if (dErr) { showError(dErr); return; }

    setIsSavingDiscount(true);
    try {
      const result = await ordenesService.updateOrden(id, { discount_amount: amount });
      if (result.success) {
        applyUpdatedTotals(result);
        showSuccess('Descuento actualizado exitosamente');
      } else {
        showError(result.message || 'Error al guardar el descuento');
      }
    } catch (err) {
      showError('Error al guardar el descuento');
    } finally {
      setIsSavingDiscount(false);
    }
  };

  const handleSaveShippingCost = async () => {
    const value = parseFloat(shippingCostInput);
    if (isNaN(value) || value < 0) {
      showError('Ingresa un valor de envío válido (mayor o igual a 0)');
      return;
    }

    setIsSavingShipping(true);
    try {
      const result = await ordenesService.updateOrden(id, { shipping_cost: value });
      if (result.success) {
        applyUpdatedTotals(result);
        showSuccess('Costo de envío actualizado exitosamente');
      } else {
        showError(result.message || 'Error al guardar el costo de envío');
      }
    } catch (err) {
      showError('Error al guardar el costo de envío');
    } finally {
      setIsSavingShipping(false);
    }
  };

  // Guarda descuento + costo de envío en una sola operación (botón "Guardar Cambios")
  const handleSaveAll = async () => {
    const { amount, error: dErr } = computeDiscountAmount();
    if (dErr) { showError(dErr); return; }
    const shipping = parseFloat(shippingCostInput);
    if (isNaN(shipping) || shipping < 0) {
      showError('Ingresa un valor de envío válido (mayor o igual a 0)');
      return;
    }

    setIsProcessing(true);
    try {
      const result = await ordenesService.updateOrden(id, {
        discount_amount: amount,
        shipping_cost: shipping
      });
      if (result.success) {
        applyUpdatedTotals(result);
        showSuccess('Cambios guardados exitosamente');
      } else {
        showError(result.message || 'Error al guardar los cambios');
      }
    } catch (err) {
      showError('Error al guardar los cambios');
    } finally {
      setIsProcessing(false);
    }
  };

  // Las cantidades solo se editan sobre una ORDEN y mientras no esté facturada.
  //
  // No sobre una PRE_ORDER: ahí las cantidades las confirma shipping en
  // quantity_confirmed, y al convertir a orden el backend sobrescribe quantity
  // con ese valor, asi que cualquier edicion manual se perderia en silencio.
  //
  // No sobre una orden facturada: el importe ya emitido al cliente dejaria de
  // cuadrar. El backend lo rechaza igualmente (409), esto solo evita ofrecerlo.
  const puedeEditarProductos =
    canManageSales &&
    orden?.order_type === 'ORDER' &&
    !orden?.has_active_invoice;

  const iniciarEdicionProductos = () => {
    setLineasEditadas((orden.items || []).map(item => ({
      clave: `existente-${item.id}`,
      id: item.id,
      product_id: item.product_id,
      descripcion: item.description || item.product_name || `Producto #${item.product_id}`,
      cantidad: String(parseFloat(item.quantity)),
      precio: String(parseFloat(item.unit_price))
    })));
    setBusquedaProducto('');
    setEditandoProductos(true);
  };

  const cancelarEdicionProductos = () => {
    setEditandoProductos(false);
    setLineasEditadas([]);
    setBusquedaProducto('');
  };

  const cambiarLinea = (clave, campo, valor) => {
    setLineasEditadas(prev => prev.map(l => (
      l.clave === clave ? { ...l, [campo]: valor } : l
    )));
  };

  const quitarLinea = (clave) => {
    setLineasEditadas(prev => prev.filter(l => l.clave !== clave));
  };

  const agregarProducto = (producto) => {
    // useProductos traduce los campos de la API al español: el nombre llega en
    // `nombreProducto` y el precio de venta en `precio`, no en name/sale_price.
    const existente = lineasEditadas.find(l => l.product_id === producto.id);
    if (existente) {
      // Si el producto ya está en la orden se suma 1 a su línea en vez de
      // duplicarla: dos líneas del mismo producto confunden al facturar.
      cambiarLinea(existente.clave, 'cantidad', String((parseFloat(existente.cantidad) || 0) + 1));
    } else {
      setLineasEditadas(prev => [...prev, {
        clave: `nueva-${producto.id}-${Date.now()}`,
        id: null,
        product_id: producto.id,
        descripcion: producto.nombreProducto,
        cantidad: '1',
        precio: String(producto.precio ?? 0)
      }]);
    }
    setBusquedaProducto('');
  };

  const totalLineaEditada = (linea) => {
    const c = parseFloat(linea.cantidad);
    const p = parseFloat(linea.precio);
    if (isNaN(c) || isNaN(p)) return 0;
    return c * p;
  };

  const handleGuardarProductos = async () => {
    if (lineasEditadas.length === 0) {
      showError('La orden debe tener al menos un producto');
      return;
    }

    const items = [];
    for (const linea of lineasEditadas) {
      const cantidad = parseFloat(linea.cantidad);
      const precio = parseFloat(linea.precio);
      if (isNaN(cantidad) || cantidad <= 0) {
        showError(`La cantidad de "${linea.descripcion}" debe ser mayor que 0`);
        return;
      }
      if (isNaN(precio) || precio < 0) {
        showError(`El precio de "${linea.descripcion}" no es válido`);
        return;
      }
      items.push({
        ...(linea.id ? { id: linea.id } : { product_id: linea.product_id }),
        quantity: cantidad,
        unit_price: precio
      });
    }

    setGuardandoProductos(true);
    try {
      // replace_items: la lista enviada es la definitiva. Sin ese flag el
      // backend interpreta "items" como actualización parcial y no borraría
      // las líneas quitadas.
      const result = await ordenesService.updateOrden(id, { replace_items: true, items });
      if (result.success) {
        // Se recarga en vez de aplicar los totales de la respuesta: el PUT
        // devuelve los totales de la venta pero no los de cada linea.
        await loadOrdenDetalle();
        cancelarEdicionProductos();
        const avisos = result.data?.stock_warnings || result.stock_warnings;
        if (avisos?.length) {
          showSuccess(`Orden actualizada. ${avisos.length} producto(s) sin stock suficiente.`);
        } else {
          showSuccess('Orden actualizada exitosamente');
        }
      } else {
        showError(result.message || 'Error al actualizar la orden');
      }
    } catch (err) {
      showError('Error al actualizar la orden');
    } finally {
      setGuardandoProductos(false);
    }
  };

  const handleConvertToOrder = () => {
    setConfirm({
      title: 'Convertir a Orden',
      message: `¿Deseas convertir la cotización ${orden.number} en una orden de compra?`,
      confirmText: 'Convertir',
      confirmClass: 'btn-success',
      onConfirm: async () => {
        setIsProcessing(true);
        try {
          const result = await ordenesService.convertToOrder(id);
          if (result.success) {
            await loadOrdenDetalle();
            showSuccess('Cotización convertida exitosamente a orden');
          } else {
            showError(result.message || 'Error al convertir la cotización');
          }
        } catch (err) {
          showError('Error al convertir la cotización');
        } finally {
          setIsProcessing(false);
        }
      }
    });
  };

  const handleApprove = () => {
    setConfirm({
      title: 'Aprobar Orden',
      message: `¿Deseas aprobar la Orden ${orden.number}?`,
      confirmText: 'Aprobar',
      confirmClass: 'btn-success',
      onConfirm: async () => {
        setIsProcessing(true);
        try {
          const result = await ordenesService.approveOrden(id);
          if (result.success) {
            await loadOrdenDetalle();
            showSuccess('Orden aprobada exitosamente');
          } else {
            showError(result.message || 'Error al aprobar la Orden');
          }
        } catch (err) {
          showError('Error al aprobar la venta');
        } finally {
          setIsProcessing(false);
        }
      }
    });
  };

  const handleRelease = () => {
    setConfirm({
      title: 'Liberar Orden',
      message: `¿Deseas liberar la orden ${orden.number}?`,
      confirmText: 'Liberar',
      confirmClass: 'btn-primary',
      onConfirm: async () => {
        setIsProcessing(true);
        try {
          const result = await ordenesService.releaseOrden(id);
          if (result.success) {
            await loadOrdenDetalle();
            showSuccess('Orden liberada exitosamente');
          } else {
            showError(result.message || 'Error al liberar la orden');
          }
        } catch (err) {
          showError('Error al liberar la orden');
        } finally {
          setIsProcessing(false);
        }
      }
    });
  };

  const handleConvertToInvoice = () => {
    setConfirm({
      title: 'Convertir a Factura',
      message: `¿Deseas convertir la venta ${orden.number} en una factura?`,
      confirmText: 'Convertir',
      confirmClass: 'btn-primary',
      onConfirm: async () => {
        setIsProcessing(true);
        try {
          const result = await ordenesService.convertToInvoice(id);
          if (result.success) {
            if (result.data?.invoice_id) {
              navigate(`/system/facturacion/${result.data.invoice_id}`);
            } else {
              await loadOrdenDetalle();
              showSuccess('Venta convertida a factura exitosamente');
            }
          } else {
            showError(result.message || 'Error al convertir la venta a factura');
          }
        } catch (err) {
          showError('Error al convertir la venta a factura');
        } finally {
          setIsProcessing(false);
        }
      }
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No definida';
    let date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      // Fecha sin hora: interpretar como LOCAL (evita el corrimiento a "ayer" en EE.UU.)
      const [y, m, d] = dateString.split('-').map(Number);
      date = new Date(y, m - 1, d);
    } else {
      date = new Date(dateString);
    }
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    return ordenUtils.getStatusColor(status);
  };

  const getStatusLabel = (status) => {
    return ordenUtils.getStatusLabel(status);
  };

  if (isLoading) {
    return <LoadingGrid count={4} message="Cargando venta..." />;
  }

  if (error && !orden) {
    return (
      <div className="error-container">
        <h2>Venta no encontrada</h2>
        <p>{error}</p>
        <button
          className="btn btn--primary"
          onClick={() => navigate('/system/ventas')}
        >
          <FiArrowLeft /> Volver a Ventas
        </button>
      </div>
    );
  }

  const orderTypeLabel = {
    'ESTIMATE': 'Cotización',
    'PRE_ORDER': 'Pre-Orden',
    'ORDER': 'Orden',
  }[orden.order_type] || 'Orden';

  return (
    <div className="factura-detalle">
      {/* Header */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={() => navigate('/system/ventas')}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          {orden.number || `VENTA #${id}`}
        </h1>

        <div className="header-actions">
          <button
            className="btn btn--outline"
            onClick={handleDownloadPdf}
            disabled={isProcessing}
          >
            <FiDownload size={16} />
            Descargar PDF
          </button>

          {/* Convertir Cotización a Orden */}
          {canManageSales && (orden.order_type === 'ESTIMATE' || orden.type === 'ESTIMATE') && orden.status !== 'CONVERTED' && (
            <button
              className="btn btn--success"
              onClick={handleConvertToOrder}
              disabled={isProcessing}
            >
              <FiShoppingCart size={16} />
              Convertir a Orden
            </button>
          )}

          {/* Aprobar orden del portal */}
          {canManageSales && orden.source === 'PORTAL' && orden.status === 'PENDING_APPROVAL' && (
            <button
              className="btn btn--success"
              onClick={handleApprove}
              disabled={isProcessing}
            >
              <FiCheck size={16} />
              Aprobar
            </button>
          )}

          {/* Liberar orden aprobada */}
          {canManageSales && orden.status === 'APPROVED' && (
            <button
              className="btn btn--info"
              onClick={handleRelease}
              disabled={isProcessing}
            >
              <FiPlayCircle size={16} />
              Liberar
            </button>
          )}

          {/* Convertir a Factura */}
          {canManageSales && ordenUtils.canConvertToInvoice(orden) && (
            <button
              className="btn btn--primary"
              onClick={handleConvertToInvoice}
              disabled={isProcessing}
            >
              <FiFileText size={16} />
              Convertir a Factura
            </button>
          )}

          {/* Guardar cambios (descuento + costo de envío) */}
          {canManageSales && (
            <button
              className="btn btn--primary"
              type="button"
              onClick={handleSaveAll}
              disabled={isProcessing}
            >
              <FiCheck size={16} />
              Guardar Cambios
            </button>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert--danger" style={{ marginBottom: '20px' }}>
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
          </div>
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => setError(null)}
          >
            Cerrar
          </button>
        </div>
      )}

      {/* Card 1: Información Principal */}
      <div className="invoice-header-card">
        <div className="invoice-header-card__left">
          <div className="invoice-icon-circle">
            <FiShoppingCart size={32} />
          </div>
          <div className="invoice-header-info">
            <h3 className="invoice-header-title">{orden.number || `VENTA #${id}`}</h3>
            <p className="invoice-header-subtitle">
              <span className={`status-badge status-badge--${getStatusColor(orden.status)}`}>
                {getStatusLabel(orden.status)}
              </span>
              {' • '}
              <span>{orderTypeLabel}</span>
              {orden.source === 'PORTAL' && ' • Portal del Cliente'}
            </p>
          </div>
        </div>

        <div className="invoice-header-card__grid">
          {/* Cliente */}
          {cliente && (
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiUser size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Cliente</label>
                <div className="invoice-field-value" style={{ fontWeight: '600' }}>
                  {cliente.company_name || cliente.full_name}
                </div>
                {cliente.tax_id && (
                  <div style={{ fontSize: '0.85em', color: '#6b7280', marginTop: '4px' }}>
                    NIT: {cliente.tax_id}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fecha de Creación */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiCalendar size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Fecha de Creación</label>
              <div className="invoice-field-value">{formatDate(orden.created_at)}</div>
            </div>
          </div>

          {/* Contacto */}
          {cliente && (cliente.email || cliente.phone) && (
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiUser size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Contacto</label>
                {cliente.email && (
                  <div className="invoice-field-value" style={{ fontSize: '0.9em' }}>{cliente.email}</div>
                )}
                {cliente.phone && (
                  <div style={{ fontSize: '0.85em', color: '#6b7280', marginTop: '2px' }}>
                    Tel: {cliente.phone}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dirección de Entrega */}
          {cliente && cliente.delivery_address && (
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiMapPin size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Dirección de Entrega</label>
                <div className="invoice-field-value" style={{ fontSize: '0.9em', lineHeight: '1.4' }}>
                  {cliente.delivery_address}
                </div>
              </div>
            </div>
          )}

          {/* Notas (si existen) */}
          {orden.notes && (
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-icon">
                <FiFileText size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas</label>
                <div className="invoice-field-value" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9em', backgroundColor: '#fef3c7', padding: '10px', borderRadius: '6px' }}>
                  {orden.notes}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card 2: Productos */}
      {orden.items && orden.items.length > 0 ? (
        <div className="invoice-table-card">
          <div className="invoice-table-header">
            <div className="table-header-icon">
              <FiPackage size={24} />
            </div>
            <div className="table-header-content">
              <h3 className="table-header-title">Productos en la Orden</h3>
              <p className="table-header-subtitle">{orden.items.length} producto(s)</p>
            </div>

            {puedeEditarProductos && (
              <div className="table-header-actions">
                {editandoProductos ? (
                  <>
                    <button
                      className="btn btn--sm btn--ghost"
                      type="button"
                      onClick={cancelarEdicionProductos}
                      disabled={guardandoProductos}
                    >
                      <FiX size={16} />
                      Cancelar
                    </button>
                    <button
                      className="btn btn--sm btn--primary"
                      type="button"
                      onClick={handleGuardarProductos}
                      disabled={guardandoProductos}
                    >
                      <FiCheck size={16} />
                      {guardandoProductos ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn--sm btn--outline"
                    type="button"
                    onClick={iniciarEdicionProductos}
                    disabled={isProcessing}
                  >
                    <FiEdit3 size={16} />
                    Editar productos
                  </button>
                )}
              </div>
            )}

            {canManageSales && orden.has_active_invoice && (
              <div className="table-header-actions">
                <span className="hint-facturada">
                  <FiAlertCircle size={14} />
                  Facturada ({orden.invoice_number}) — para corregirla, anula la factura primero
                </span>
              </div>
            )}
          </div>

          <div className="invoice-table-wrapper">
            <table className="invoice-table">
              <thead className="invoice-table-head">
                <tr>
                  <th className="th-product">Producto</th>
                  <th className="th-qty">Cantidad</th>
                  <th className="th-price">Precio Unit.</th>
                  <th className="th-total">Total</th>
                  {editandoProductos && <th className="th-acciones"></th>}
                </tr>
              </thead>
              <tbody className="invoice-table-body">
                {/* En edición se pinta la copia de trabajo (lineasEditadas), no
                    orden.items: así agregar, quitar o cambiar importes no toca
                    nada hasta que se pulsa Guardar. */}
                {editandoProductos ? lineasEditadas.map((linea) => (
                  <tr key={linea.clave} className="invoice-table-row">
                    <td className="td-product">
                      <div className="product-cell">
                        <div className="product-cell-icon">
                          <FiPackage size={16} />
                        </div>
                        <div className="product-cell-info">
                          <div className="product-cell-name">{linea.descripcion}</div>
                          <div className="product-cell-code">
                            {linea.id ? `ID: ${linea.product_id}` : 'Nuevo'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="td-qty">
                      <input
                        type="number"
                        className="form-control qty-input"
                        min="0"
                        step="any"
                        value={linea.cantidad}
                        onChange={(e) => cambiarLinea(linea.clave, 'cantidad', e.target.value)}
                        disabled={guardandoProductos}
                        aria-label={`Cantidad de ${linea.descripcion}`}
                      />
                    </td>
                    <td className="td-price">
                      <input
                        type="number"
                        className="form-control price-input"
                        min="0"
                        step="any"
                        value={linea.precio}
                        onChange={(e) => cambiarLinea(linea.clave, 'precio', e.target.value)}
                        disabled={guardandoProductos}
                        aria-label={`Precio de ${linea.descripcion}`}
                      />
                    </td>
                    <td className="td-total">
                      <span className="total-value">{formatCurrency(totalLineaEditada(linea))}</span>
                    </td>
                    <td className="td-acciones">
                      <button
                        type="button"
                        className="btn-quitar-linea"
                        onClick={() => quitarLinea(linea.clave)}
                        disabled={guardandoProductos}
                        title={`Quitar ${linea.descripcion}`}
                        aria-label={`Quitar ${linea.descripcion}`}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </td>
                  </tr>
                )) : orden.items.map((item, index) => (
                  <tr key={index} className="invoice-table-row">
                    <td className="td-product">
                      <div className="product-cell">
                        <div className="product-cell-icon">
                          <FiPackage size={16} />
                        </div>
                        <div className="product-cell-info">
                          <div className="product-cell-name">
                            {item.description || item.product_name || `Producto #${item.product_id}`}
                          </div>
                          {item.product_id && (
                            <div className="product-cell-code">ID: {item.product_id}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="td-qty">
                      <span className="qty-badge">{parseFloat(item.quantity)}</span>
                    </td>
                    <td className="td-price">
                      <span className="price-value">{formatCurrency(item.unit_price)}</span>
                    </td>
                    <td className="td-total">
                      <span className="total-value">{formatCurrency(item.subtotal || item.total)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Agregar productos a la orden */}
          {editandoProductos && (
            <div className="agregar-producto">
              <div className="agregar-producto__buscador">
                <FiSearch size={16} />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar un producto para agregarlo a la orden..."
                  value={busquedaProducto}
                  onChange={(e) => setBusquedaProducto(e.target.value)}
                  disabled={guardandoProductos}
                />
              </div>

              {busquedaProducto.trim().length > 0 && (
                <ul className="agregar-producto__resultados">
                  {(productosEncontrados || []).slice(0, 8).map(p => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => agregarProducto(p)}
                        disabled={guardandoProductos}
                      >
                        <FiPlus size={14} />
                        <span className="nombre">{p.nombreProducto}</span>
                        <span className="precio">{formatCurrency(p.precio ?? 0)}</span>
                      </button>
                    </li>
                  ))}
                  {(productosEncontrados || []).length === 0 && (
                    <li className="sin-resultados">Sin resultados para "{busquedaProducto}"</li>
                  )}
                </ul>
              )}
            </div>
          )}

          {/* Totales */}
          <div className="invoice-totals-section">
            <div className="totals-container">
              {/* Descuento editable */}
              <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e5e7eb' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>
                  Descuento
                </label>

                {/* Selector de tipo de descuento */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <button
                    onClick={() => {
                      setDiscountType('amount');
                      setDiscountInput('0');
                    }}
                    style={{
                      flex: 1,
                      padding: '6px 12px',
                      border: '2px solid',
                      borderColor: discountType === 'amount' ? '#7B2D8E' : '#e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: discountType === 'amount' ? '600' : '400',
                      color: discountType === 'amount' ? '#7B2D8E' : '#6b7280',
                      backgroundColor: discountType === 'amount' ? '#f3e8f7' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Monto ($)
                  </button>
                  <button
                    onClick={() => {
                      setDiscountType('percentage');
                      setDiscountInput('0');
                    }}
                    style={{
                      flex: 1,
                      padding: '6px 12px',
                      border: '2px solid',
                      borderColor: discountType === 'percentage' ? '#7B2D8E' : '#e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: discountType === 'percentage' ? '600' : '400',
                      color: discountType === 'percentage' ? '#7B2D8E' : '#6b7280',
                      backgroundColor: discountType === 'percentage' ? '#f3e8f7' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Porcentaje (%)
                  </button>
                </div>

                {/* Input de descuento */}
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    min="0"
                    max={discountType === 'percentage' ? 100 : orden.subtotal}
                    step={discountType === 'percentage' ? '1' : '1000'}
                    value={discountInput}
                    onChange={e => setDiscountInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveDiscount();
                      if (e.key === 'Escape') setDiscountInput('0');
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      paddingRight: '36px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                    placeholder="0"
                  />
                  <span style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    pointerEvents: 'none'
                  }}>
                    {discountType === 'percentage' ? '%' : '$'}
                  </span>
                </div>

                {/* Mostrar el monto calculado si es porcentaje */}
                {discountType === 'percentage' && parseFloat(discountInput) > 0 && (
                  <div style={{ marginTop: '6px', fontSize: '0.75rem', color: '#6b7280' }}>
                    Descuento: ${(orden.subtotal * (parseFloat(discountInput) / 100)).toFixed(2)}
                  </div>
                )}

                {/* Botón de guardar */}
                <button
                  onClick={handleSaveDiscount}
                  disabled={isSavingDiscount || !canManageSales}
                  style={{
                    marginTop: '8px',
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#7B2D8E',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: isSavingDiscount ? 'not-allowed' : 'pointer',
                    opacity: isSavingDiscount ? 0.6 : 1
                  }}
                >
                  {isSavingDiscount ? 'Guardando...' : 'Aplicar descuento'}
                </button>
              </div>

              {/* Costo de envío editable */}
              <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e5e7eb' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>
                  Costo de Envío
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    min="0"
                    step="1000"
                    value={shippingCostInput}
                    onChange={e => setShippingCostInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveShippingCost();
                      if (e.key === 'Escape') setShippingCostInput(String(orden.shipping_cost || 0));
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      paddingRight: '36px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                    placeholder="0"
                  />
                  <span style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    pointerEvents: 'none'
                  }}>
                    $
                  </span>
                </div>
                <button
                  onClick={handleSaveShippingCost}
                  disabled={isSavingShipping || !canManageSales}
                  style={{
                    marginTop: '8px',
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#7B2D8E',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: isSavingShipping ? 'not-allowed' : 'pointer',
                    opacity: isSavingShipping ? 0.6 : 1
                  }}
                >
                  {isSavingShipping ? 'Guardando...' : 'Aplicar costo de envío'}
                </button>
              </div>

              <div className="totals-row">
                <span className="totals-label">Subtotal</span>
                <span className="totals-value">{formatCurrency(orden.subtotal)}</span>
              </div>

              {orden.discount_amount > 0 && (
                <div className="totals-row totals-row--discount">
                  <span className="totals-label">Descuento</span>
                  <span className="totals-value">-{formatCurrency(orden.discount_amount)}</span>
                </div>
              )}

              <div className="totals-row">
                <span className="totals-label">Envío</span>
                <span className="totals-value">{formatCurrency(orden.shipping_cost || 0)}</span>
              </div>

              <div className="totals-divider"></div>

              <div className="totals-row totals-row--final">
                <span className="totals-label">Total</span>
                <span className="totals-value">{formatCurrency(orden.total)}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="invoice-empty-state">
          <div className="empty-state-icon">
            <FiPackage size={64} />
          </div>
          <h3 className="empty-state-title">No hay productos en esta venta</h3>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          isOpen={true}
          title={confirm.title}
          message={confirm.message}
          confirmText={confirm.confirmText || 'Confirmar'}
          confirmClass={confirm.confirmClass || 'btn-primary'}
          onConfirm={acceptConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

export default VentaDetalle;
